import { Typography } from '@material-ui/core';
import PropTypes from 'prop-types';
import React from 'react';
import GS from '../grafsync/grafsync';
import LabelledSwitch from './LabelledSwitch';
import MenuSelector from './MenuSelector';
import PrimaryButton from './PrimaryButton';
import TitledPanel from './TitledPanel';
import Warning from './Warning';
import LoadingButton from './LoadingButton';
import { makeCancelable } from './utils/utils';

const menuOptions = [
    {
        "value": "application/ld+json",
        "label": "JSON-LD"
    },
    {
        "value": "text/turtle",
        "label": "Turtle"
    },
    {
        "value": "application/rdf+xml",
        "label": "RDF/XML"
    }
    ,
    {
        "value": "application/n-triples",
        "label": "N-triples"
    }
];

class DataDownload extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            includeIRI: true,
            includeXL: props.includeXL,
            includeAnnotations: props.includeAnnotations,
            isLoading: false,
            isErrored: false,
            format: "application/ld+json"
        };
        this.cancellable = null;
    }

    doDownload(exportName, resource, graph, format, options, embed) {
        this.cancellable = makeCancelable(GS.download(exportName, resource, graph, format, options, embed));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    handleChange = (format) => this.setState({ format });

    handleXlToggleChange = checked => this.setState({ includeXL: checked });

    handleAnnotationToggleChange = checked => this.setState({ includeAnnotations: checked });

    downloadData = async () => {
        this.setState({ isLoading: true });
        const options = [];
        if (this.state.includeXL) options.push("includeXL");
        if (this.state.includeAnnotations) options.push("includeAnnotation");
        this.doDownload(this.props.exportName, this.props.resource,
            this.props.graph, this.state.format, options, true)
            .promise
            .then(() => {
                if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isLoading: false });
            })
            .catch((e) => {
                if (this.cancellable && !this.cancellable.isCancelled())
                    this.setState({
                        isErrored: true,
                        isLoading: false,
                        format: "application/ld+json",
                        errorMessage: e.message.includes("The payload is too large.") ?
                            e.message
                            :
                            "Could not download data for export."
                    });
            });
    };

    onExitWarning = () => this.setState({ isErrored: false });

    render() {

        const { skosXLoption, annotationOption, message, title, fullHeight, dataTest } = this.props;
        const { includeXL, includeAnnotations, isErrored, isLoading, errorMessage } = this.state;

        if (isErrored) return <Warning message={errorMessage} onExitWarning={this.onExitWarning} />;

        return (
            <div style={{
                maxWidth: 400,
                ...(fullHeight) && { height: '100%' }
            }}>
                <TitledPanel dataTest={dataTest} fontSize="large" height="full" title={title}>
                    <div style={{ minHeight: '3em' }}>
                        <Typography variant="body2" paragraph style={{ color: '#212121' }}>{message}</Typography>
                    </div>
                    <MenuSelector dataTest="format-selector" label="Format" handleChange={this.handleChange} selected="application/ld+json" hintWord="format" options={menuOptions} />
                    {
                        skosXLoption &&
                        <LabelledSwitch
                            dataTest="includeXL"
                            label="Include XL Labels"
                            checked={includeXL}
                            handleChange={this.handleXlToggleChange}
                        />
                    }
                    {
                        annotationOption &&
                        <LabelledSwitch
                            dataTest="includeAnnotation"
                            label="Include Annotations"
                            checked={includeAnnotations}
                            handleChange={this.handleAnnotationToggleChange}
                        />
                    }
                    {!skosXLoption && !annotationOption && <div style={{ marginTop: '57px' }} />}
                    <div style={{marginLeft: '2px', marginBottom: '2px'}}>
                    {isLoading ?
                        <LoadingButton disabled />
                        :
                        <PrimaryButton disabled={isLoading} onClick={this.downloadData}>Download</PrimaryButton>
                    }
                    </div>
                </TitledPanel>
            </div>
        );
    }
}

DataDownload.defaultProps = {
    skosXLoption: false,
    annotationOption: false,
    fullHeight: false,
    includeXL: false,
    includeAnnotations: false,
    title: "Download Data",
    dataTest: "data-download"
};

DataDownload.propTypes = {
    dataTest: PropTypes.string,
    title: PropTypes.string,
    fullHeight: PropTypes.bool,
    includeXL: PropTypes.bool,
    includeAnnotations: PropTypes.bool,
    resource: PropTypes.string.isRequired,
    exportName: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    graph: PropTypes.string.isRequired,
    skosXLoption: PropTypes.bool,
    annotationOption: PropTypes.bool
};

export default DataDownload;